import http from "@/helper/http";

const initialState = () => ({
  releases: [],
  totalReleases: 0,
  loadingReleases: false,
  loadingUpload: false,
  currentPage: 1,
  perPage: 20,
  platformFilter: "",
  productFilter: "",
  flavorFilter: "",
});

export default {
  namespaced: true,
  state: initialState(),
  mutations: {
    SET_RELEASES(state, { releases, total }) {
      state.releases = releases || [];
      state.totalReleases = total || 0;
    },
    SET_LOADING_RELEASES(state, loading) {
      state.loadingReleases = loading;
    },
    SET_LOADING_UPLOAD(state, loading) {
      state.loadingUpload = loading;
    },
    SET_PAGE(state, page) {
      state.currentPage = page;
    },
    SET_PLATFORM_FILTER(state, platform) {
      state.platformFilter = platform;
    },
    SET_PRODUCT_FILTER(state, product) {
      state.productFilter = product;
    },
    SET_FLAVOR_FILTER(state, flavor) {
      state.flavorFilter = flavor;
    },
    UPDATE_RELEASE(state, updatedRelease) {
      const idx = state.releases.findIndex((r) => r.id === updatedRelease.id);
      if (idx !== -1) {
        state.releases.splice(idx, 1, updatedRelease);
      }
    },
    REMOVE_RELEASE(state, releaseId) {
      state.releases = state.releases.filter((r) => r.id !== releaseId);
      state.totalReleases = Math.max(0, state.totalReleases - 1);
    },
    RESET_STATE(state) {
      Object.assign(state, initialState());
    },
  },
  actions: {
    async fetchReleases({ commit, state }) {
      commit("SET_LOADING_RELEASES", true);
      try {
        const params = {
          page: state.currentPage,
          per_page: state.perPage,
        };
        if (state.platformFilter) {
          params.platform = state.platformFilter;
        }
        if (state.productFilter) {
          params.product = state.productFilter;
        }
        if (state.flavorFilter) {
          params.flavor = state.flavorFilter;
        }
        const response = await http.get("/v1/admin/releases", { params });
        commit("SET_RELEASES", {
          releases: response?.data?.releases || [],
          total: response?.data?.total || 0,
        });
      } finally {
        commit("SET_LOADING_RELEASES", false);
      }
    },
    async uploadRelease({ commit, dispatch }, formData) {
      commit("SET_LOADING_UPLOAD", true);
      try {
        const response = await http.post("/v1/admin/releases", formData, {
          headers: { "Content-Type": undefined },
        });
        await dispatch("fetchReleases");
        return response?.data;
      } finally {
        commit("SET_LOADING_UPLOAD", false);
      }
    },
    async createReleaseMetadata({ commit, dispatch }, payload) {
      commit("SET_LOADING_UPLOAD", true);
      try {
        const response = await http.post(
          "/v1/admin/releases/metadata",
          payload,
        );
        await dispatch("fetchReleases");
        return response?.data;
      } finally {
        commit("SET_LOADING_UPLOAD", false);
      }
    },
    async checkReleaseExists(_, { version, platform, product, flavor }) {
      const response = await http.get("/v1/admin/releases/check", {
        params: { version, platform, product, flavor },
      });
      return response?.data;
    },
    async updateRelease({ commit }, { releaseId, payload }) {
      const response = await http.patch(
        `/v1/admin/releases/${releaseId}`,
        payload,
      );
      if (response?.data) {
        commit("UPDATE_RELEASE", response.data);
      }
      return response?.data;
    },
    async deleteRelease({ commit }, releaseId) {
      await http.delete(`/v1/admin/releases/${releaseId}`);
      commit("REMOVE_RELEASE", releaseId);
    },
    setPage({ commit, dispatch }, page) {
      commit("SET_PAGE", page);
      dispatch("fetchReleases");
    },
    setPlatformFilter({ commit, dispatch }, platform) {
      commit("SET_PLATFORM_FILTER", platform);
      commit("SET_PAGE", 1);
      dispatch("fetchReleases");
    },
    setProductFilter({ commit, dispatch }, product) {
      commit("SET_PRODUCT_FILTER", product);
      commit("SET_PAGE", 1);
      dispatch("fetchReleases");
    },
    setFlavorFilter({ commit, dispatch }, flavor) {
      commit("SET_FLAVOR_FILTER", flavor);
      commit("SET_PAGE", 1);
      dispatch("fetchReleases");
    },
  },
  getters: {
    releases: (state) => state.releases,
    totalReleases: (state) => state.totalReleases,
    loadingReleases: (state) => state.loadingReleases,
    loadingUpload: (state) => state.loadingUpload,
    currentPage: (state) => state.currentPage,
    perPage: (state) => state.perPage,
    platformFilter: (state) => state.platformFilter,
    productFilter: (state) => state.productFilter,
    flavorFilter: (state) => state.flavorFilter,
    totalPages: (state) => Math.ceil(state.totalReleases / state.perPage),
  },
};
